import { apiUrl } from "@workspace/base-api/apiUrl";
import {
	addScopedAuthTokenProvider,
	fetchJson,
} from "@workspace/base-api/fetchJson";

// HA-831 — the EPOS staff session. Its token authenticates only the EPOS routes, at its venue, as the
// staff member's role there; the server refuses it anywhere else. So it lives in its own slot (it
// used to take over the tab as an admin "impersonation", on every call) and is sent only on the
// requests it is for. Everything else goes out on the till's own token.

const STORAGE_KEY = "epos_staff_session";

export interface EposStaffSession {
	token: string;
	expires: string;
	sessionId: string;
	name: string;
	tenant: string;
	venueId: string;
}

function read(): EposStaffSession | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const session = JSON.parse(raw) as EposStaffSession;
		if (new Date(session.expires) <= new Date()) {
			sessionStorage.removeItem(STORAGE_KEY);
			return null;
		}
		return session;
	} catch {
		return null;
	}
}

const escape = (value: string) =>
	encodeURIComponent(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The routes the server marks `[KioskStaffRoute]`, at this session's venue. */
export function isEposStaffRequest(url: string, venueId: string): boolean {
	let path: string;
	try {
		path = new URL(url, "http://local").pathname;
	} catch {
		return false;
	}
	const venue = escape(venueId);
	return [
		new RegExp(`^/venue-management/${venue}/epos(/|$)`),
		new RegExp(
			`^/venue-management/stock/venues/${venue}/86(/(status|clear|override))?$`,
		),
		/^\/integrations\/stripe\/start-payment$/,
		/^\/integrations\/stripe\/payments\/[^/]+(\/(cancel|completed))?$/,
		// HA-868: the Terminal connection token, for the card payment this session takes.
		/^\/integrations\/stripe\/terminal\/connection-token$/,
		new RegExp(`^/venue-management/${venue}/kiosk/staff-session/[^/]+$`),
	].some((route) => route.test(path));
}

addScopedAuthTokenProvider((url) => {
	const session = read();
	return session && isEposStaffRequest(url, session.venueId)
		? session.token
		: null;
});

export function activateStaffToken(
	result: { token: string; expires: string; sessionId: string; name: string },
	tenant: string,
	venueId: string,
) {
	const session: EposStaffSession = { ...result, tenant, venueId };
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function currentStaffSession(): EposStaffSession | null {
	return read();
}

/**
 * Ends the session at the server (switching away, locking, logging out) and forgets it here. The
 * till starting the next session also ends this one, so a failed call leaves nothing usable behind.
 */
export async function deactivateStaffToken(): Promise<void> {
	const session = read();
	if (!session) return;

	try {
		await fetchJson(
			new URL(
				`/venue-management/${session.venueId}/kiosk/staff-session/${session.sessionId}`,
				apiUrl,
			).toString(),
			{ method: "DELETE", headers: { tenant: session.tenant } },
		);
	} catch {
		// Best effort: the next session or the 12h cap ends it at the server.
	} finally {
		sessionStorage.removeItem(STORAGE_KEY);
	}
}

/**
 * The EPOS staff switch: start the session, then check they may use the EPOS. A refusal ends the
 * session again: it used to leave the token live in the tab behind the "no permission" message.
 */
export async function startStaffSession(options: {
	mint: () => Promise<{
		token: string;
		expires: string;
		sessionId: string;
		name: string;
	}>;
	mayUseEpos: () => Promise<boolean>;
	tenant: string;
	venueId: string;
}): Promise<"started" | "not-started" | "refused"> {
	let minted;
	try {
		minted = await options.mint();
	} catch {
		return "not-started";
	}
	activateStaffToken(minted, options.tenant, options.venueId);

	let allowed = false;
	try {
		allowed = await options.mayUseEpos();
	} finally {
		if (!allowed) await deactivateStaffToken();
	}
	return allowed ? "started" : "refused";
}

/**
 * HA-831 part 2 — the EPOS staff switch with a PIN: the PIN buys a single-use ticket for this staff
 * member (HA-829), the ticket starts their session, and the EPOS permission check follows
 * ({@link startStaffSession}). A wrong PIN starts nothing and says why.
 */
export async function switchWithPin(options: {
	staff: { id: string; registeredUserId: string };
	pin: string;
	checkPin: (data: {
		registeredUserId: string;
		pin: string;
	}) => Promise<
		| { success: true; data: { ticket: string } }
		| { success: false; status?: number; code?: string }
	>;
	start: (data: { staffMemberId: string; ticket: string }) => Promise<{
		token: string;
		expires: string;
		sessionId: string;
		name: string;
	}>;
	mayUseEpos: () => Promise<boolean>;
	tenant: string;
	venueId: string;
}): Promise<
	| { outcome: "pin-failed"; failure: { status?: number; code?: string } }
	| { outcome: "started" | "not-started" | "refused" }
> {
	const checked = await options.checkPin({
		registeredUserId: options.staff.registeredUserId,
		pin: options.pin,
	});
	if (!checked.success) return { outcome: "pin-failed", failure: checked };

	const outcome = await startStaffSession({
		mint: () =>
			options.start({
				staffMemberId: options.staff.id,
				ticket: checked.data.ticket,
			}),
		mayUseEpos: options.mayUseEpos,
		tenant: options.tenant,
		venueId: options.venueId,
	});
	return { outcome };
}

if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", () => {
		sessionStorage.removeItem(STORAGE_KEY);
	});
}
